import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import toast from "react-hot-toast";
import claimsGQL from "../../../gql/claims";
import "../styles/pre-auth.css";
import "../../Beneficiary/styles/BeneficiaryList.css";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Modal,
  Box,
  Grid,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import ClaimHistoryFilter from "../ClaimHistoryFilter";
import PDFDownload from "./components/PDFDownload";
import VatInvoice from "./components/VatInvoice";
import { useTranslation } from "react-i18next";
import AppButton from "../../../shared/AppButton";

const parseJwtPayload = () => {
  try {
    const rawToken = localStorage.getItem("token")?.replaceAll('"', "");
    if (!rawToken) {
      return {};
    }

    const payload = rawToken.split(".")[1];
    if (!payload) {
      return {};
    }

    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = decodeURIComponent(
      atob(normalized)
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(""),
    );

    return JSON.parse(decoded);
  } catch (error) {
    return {};
  }
};
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  overflowY: "auto",
  maxHeight: "80vh",
};

function GssReportMain() {
  const { t } = useTranslation();
  const currentDate = new Date();
  const [selectedPayerReport, setSelectedPayerReport] = useState(null);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const tokenUser = parseJwtPayload();
  const providerCchi =
    user?.ProvidersCCHI ||
    user?.providers_cchi ||
    user?.ProvidersCchi ||
    user?.Cchi ||
    user?.cchi ||
    tokenUser?.ProvidersCCHI ||
    tokenUser?.providers_cchi ||
    tokenUser?.ProvidersCchi ||
    "___";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVatModalOpen, setIsVatModalOpen] = useState(false);

  const currentYearMonth = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth(),
  ).padStart(2, "0")}`;
  const initialDates = getFirstAndLastDateOfMonth(currentYearMonth);
  const [showFilter, setShowFilter] = useState(false);
  const [fromDate, setFromDate] = useState(initialDates.firstDate);
  const [toDate, setToDate] = useState(initialDates.lastDate);

  const [headerInfo, setHeaderInfo] = useState({
    providerCode: "___",
    providerName: user?.ProvidersNameEn || user?.FullName || "___",
    cchiNo: providerCchi,
    batchMonth: new Date().toISOString().slice(0, 7).replace("-", ""),
    batchId: "___",
    batchType: "Regular",
    claimType: "___",
    totalClaims: 0,
    reportPeriod: "___",
    payerName: "___",
  });

  function getFirstAndLastDateOfMonth(yearMonth) {
    const [year, month] = yearMonth.split("-").map(Number);
    const firstDate = new Date(year, month - 1, 1);
    const lastDate = new Date(year, month, 0);

    const firstDateString = `${firstDate.getFullYear()}-${(
      firstDate.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${firstDate.getDate().toString().padStart(2, "0")}`;
    const lastDateString = `${lastDate.getFullYear()}-${(
      lastDate.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${lastDate.getDate().toString().padStart(2, "0")}`;

    return {
      firstDate: firstDateString,
      lastDate: lastDateString,
    };
  }

  const { error, data, refetch } = useQuery(claimsGQL.CLAIMS_GSS_REPORT, {
    variables: {
      input: {
        from_date: fromDate,
        to_date: toDate,
      },
    },
  });

  useEffect(() => {
    if (error?.graphQLErrors?.[0]?.message) {
      toast.error(error.graphQLErrors[0].message);
    }
  }, [error]);

  const getLastNMonths = (n) => {
    const months = [];
    for (let i = 0; i < n; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1,
      );
      months.push(date);
    }
    return months;
  };

  const handleMonthClick = (date) => {
    const yearMonth = `${date.getFullYear()}-${String(
      date.getMonth() + 1,
    ).padStart(2, "0")}`;
    const { firstDate, lastDate } = getFirstAndLastDateOfMonth(yearMonth);

    setFromDate(firstDate);
    setToDate(lastDate);

    setHeaderInfo((prev) => ({
      ...prev,
      batchMonth: firstDate.slice(0, 7).replace("-", ""),
      reportPeriod: `${firstDate} - ${lastDate}`,
    }));

    refetch({
      input: {
        from_date: firstDate,
        to_date: lastDate,
      },
    });
  };

  const monthButtons = getLastNMonths(13).map((date, index) => {
    const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    const { firstDate } = getFirstAndLastDateOfMonth(yearMonth);
    const isActive = firstDate === fromDate;

    return (
      <AppButton
        key={index}
        variant={isActive ? "contained" : "outlined"}
        size="small"
        onClick={() => handleMonthClick(date)}
        label={`${date.getFullYear()} ${t(date.toLocaleString("default", { month: "long" }))}`}
        className={`mb-2 !text-[11px] !py-0.5 whitespace-nowrap ${
          isActive
            ? "!bg-[#0070C2] !text-white"
            : "!border-primary !text-primary hover:!bg-primary/10"
        }`}
      />
    );
  });

  const handleShowReport = (payer) => {
    const claimTypeText = (payer?.claim_type_counts || [])
      .map((item) => item?.claim_type)
      .filter(Boolean)
      .join(", ");

    setSelectedPayerReport(payer);
    setHeaderInfo((prev) => ({
      ...prev,
      payerName: payer?.payer_name || "___",
      providerCode:
        user?.ProvidersBranchID ||
        user?.ProvidersID ||
        user?.providers_id ||
        "___",
      providerName:
        user?.ProvidersNameEn || user?.FullName || user?.full_name || "___",
      cchiNo: providerCchi,
      batchMonth: fromDate.slice(0, 7).replace("-", ""),
      batchId: payer?.payer_id || "___",
      batchType: "Regular",
      claimType: claimTypeText || "___",
      totalClaims: payer?.total_claims || 0,
      reportPeriod: `${fromDate} - ${toDate}`,
    }));
    setIsModalOpen(true);
  };

  const handleShowVatInvoice = (payer) => {
    setSelectedPayerReport(payer);
    setIsVatModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsVatModalOpen(false);
    setSelectedPayerReport(null);
  };

  return (
    <>
      <div className="pre_auth_container !p-0">
        <div className="flex justify-between flex-col lg:flex-row gap-2 mb-2">
          <div className="">
            <Tooltip
              placement="right"
              title={t("Keep track of your GSS Report")}
              className="my-auto !mt-2"
            >
              <span className="font-semibold text-black/70 text-xl ">
                {t("GSS Report")}
                <span className="text-lg ml-1">
                  ({data?.getGssReports?.data?.length || 0}{" "}
                  <span className="text-xs font-bold italic">
                    {t("Entries")}
                  </span>
                  )
                </span>
              </span>
            </Tooltip>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-2 ">{monthButtons}</div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
          {data?.getGssReports?.data?.length > 0 ? (
            data.getGssReports.data.map((payer, index) => (
              <div
                key={index}
                className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="bg-gray-100 px-2 py-2 border-b border-gray-300">
                  <Typography
                    className="!text-[12px] !font-bold !text-slate-700 !uppercase !tracking-tight"
                    title={payer.payer_name}
                  >
                    {payer.payer_name}
                  </Typography>
                </div>

                <div className="p-3">
                  <div className="flex justify-between items-center mb-3 bg-blue-50/50 p-2 rounded-md">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      {t("Number of Reports")}
                    </span>
                    <span className="text-[12px] font-bold text-[#0070C2]">
                      {payer.reports.length}
                    </span>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <AppButton
                      variant="outlined"
                      size="small"
                      fullWidth
                      className="!text-[11px] !py-1 !border-blue-200 hover:!bg-blue-50"
                      onClick={() => handleShowReport(payer)}
                      label={t("Show Report")}
                    />
                    <AppButton
                      variant="contained"
                      size="small"
                      fullWidth
                      className="!text-[11px] !py-1 !bg-[#0070C2]"
                      onClick={() => handleShowVatInvoice(payer)}
                      label={t("VAT Invoice")}
                    />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full py-6 text-center bg-slate-50 rounded-lg border border-dashed border-slate-300">
              <Typography variant="body2" className="!text-slate-400 ">
                {t("No reports available for this period.")}
              </Typography>
            </div>
          )}
        </div>

        <Modal
          open={isModalOpen}
          onClose={closeModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={modalStyle}>
            <PDFDownload payer={selectedPayerReport} headerInfo={headerInfo} />
            <div className="flex justify-end my-6">
              <Button variant="outlined" color="primary" onClick={closeModal}>
                {t("Close")}
              </Button>
            </div>
          </Box>
        </Modal>

        <Modal
          open={isVatModalOpen}
          onClose={closeModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={modalStyle}>
            <VatInvoice reports={selectedPayerReport} />
            <div className="flex justify-end my-6">
              <Button variant="outlined" color="primary" onClick={closeModal}>
                {t("Close")}
              </Button>
            </div>
          </Box>
        </Modal>

        <ClaimHistoryFilter
          setShowFilter={setShowFilter}
          showFilter={showFilter}
          refetch={refetch}
        />
      </div>
    </>
  );
}

export default GssReportMain;
